import { useState, useEffect } from 'react'
import { useAuth } from '../lib/AuthContext'
import { supabase, getUserCompanyId } from '../lib/supabase'

// ─── Data layer ─────────────────────────────────────────────────────────

async function loadVision(companyId) {
  if (!companyId) return null
  const { data } = await supabase.from('vision_maps').select('*').eq('company_id', companyId).maybeSingle()
  return data
}

async function saveVision(companyId, data) {
  if (!companyId) return
  const { data: { session } } = await supabase.auth.getSession()
  const userId = session?.user?.id
  await supabase.from('vision_maps').upsert(
    { company_id: companyId, user_id: userId, vision_data: data, updated_at: new Date().toISOString() },
    { onConflict: 'company_id' }
  )
}

// ─── Schema ─────────────────────────────────────────────────────────────
// The Hemlock Blueprint — 3 layers, 6 elements.
// See HG-Worksheets/HG-Blueprint.pdf for the canonical paper version.

const DEFAULT = {
  // ─── IDENTITY ───
  coreValues: ['', '', '', '', ''],
  purpose: '',

  // ─── STRATEGY ───
  winningAspiration: '',
  aspirationDetail: '',

  wherePlaySegment: '',
  wherePlayChannel: '',
  wherePlayProduct: '',
  wherePlayWont: '',

  howWeWinPowers: [],          // array of power ids (see POWERS below)
  howWeWinOther: '',           // detail when "other" is selected
  howWeWinDescription: '',     // plain-language description

  // ─── EXECUTION ───
  year: '',
  revenueTarget: '',
  keyMetric: '',
  goals: [
    { text: '', owner: '' },
    { text: '', owner: '' },
    { text: '', owner: '' },
  ],
  notAbout: '',

  // ─── Legacy fields (preserved for backwards compatibility) ───
  // Founding insight section was removed from the UI in May 2026.
  // Data is preserved here so nothing is lost; can be repromoted later.
  problemWeSaw: '',
  whyNow: '',
  whyUs: '',
  // Old field names; migrated on load to their new equivalents below.
  northStar: '',
  northStarLooksLike: '',
  idealCustomer: '',
  playingField: '',
  whatMakesUsDifferent: ['', '', ''],
  howWeDeliver: '',
  revenueGoal: '',
  initiatives: ['', '', '', '', ''],
}

// One-time soft migration when loading old data.
// If a new-name field is empty but the corresponding old-name field has
// content, copy it over so the user sees their data in the new UI.
function migrateLoaded(raw) {
  if (!raw) return JSON.parse(JSON.stringify(DEFAULT))
  const merged = { ...JSON.parse(JSON.stringify(DEFAULT)), ...raw }

  if (!merged.winningAspiration && merged.northStar) merged.winningAspiration = merged.northStar
  if (!merged.aspirationDetail && merged.northStarLooksLike) merged.aspirationDetail = merged.northStarLooksLike
  if (!merged.wherePlaySegment && merged.idealCustomer) merged.wherePlaySegment = merged.idealCustomer
  if (!merged.wherePlayChannel && merged.playingField) merged.wherePlayChannel = merged.playingField
  if (!merged.howWeWinDescription && merged.howWeDeliver) merged.howWeWinDescription = merged.howWeDeliver
  if (!merged.revenueTarget && merged.revenueGoal) merged.revenueTarget = merged.revenueGoal

  // initiatives (string[]) → goals ({text, owner}[])
  if ((!merged.goals || merged.goals.every(g => !g.text)) && (merged.initiatives || []).some(s => s)) {
    merged.goals = merged.initiatives.filter(Boolean).map(t => ({ text: t, owner: '' }))
    while (merged.goals.length < 3) merged.goals.push({ text: '', owner: '' })
  }

  return merged
}

// Helmer's 7 Powers — durable sources of competitive advantage.
const POWERS = [
  { id: 'counter-positioning', name: 'Counter-positioning', desc: "A new business model the incumbent can't copy without breaking their own." },
  { id: 'scale-economies',     name: 'Scale economies',     desc: 'Costs per unit fall as we grow. Volume is the moat.' },
  { id: 'network-economies',   name: 'Network economies',   desc: 'Each new user makes the product more valuable for the others.' },
  { id: 'switching-costs',     name: 'Switching costs',     desc: 'Leaving us is more painful than staying.' },
  { id: 'brand',               name: 'Brand',               desc: 'Our name creates real willingness-to-pay or trust above unbranded alternatives.' },
  { id: 'cornered-resource',   name: 'Cornered resource',   desc: 'Exclusive access to a person, contract, dataset, or technology.' },
  { id: 'process-power',       name: 'Process power',       desc: 'A way of operating that took years to build and would take a competitor as long to match.' },
  { id: 'other',               name: 'Other / hybrid',      desc: "Name it, then defend why it's durable." },
]

// ─── Component ──────────────────────────────────────────────────────────

export default function VisionMap({ previewUserId }) {
  const { user: authUser } = useAuth()
  const user = previewUserId ? { ...authUser, id: previewUserId } : authUser
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saveStatus, setSaveStatus] = useState('saved')
  const [dbReady, setDbReady] = useState(true)
  const [activeTab, setActiveTab] = useState('identity')
  const [companyId, setCompanyId] = useState(previewUserId || null)
  const [companyIdResolved, setCompanyIdResolved] = useState(!!previewUserId)

  useEffect(() => {
    if (previewUserId) { setCompanyId(previewUserId); setCompanyIdResolved(true); return }
    if (!authUser?.email) return
    let alive = true
    getUserCompanyId(authUser.email).then(cid => { if (!alive) return; setCompanyId(cid); setCompanyIdResolved(true) })
    return () => { alive = false }
  }, [authUser?.email, previewUserId])

  useEffect(() => {
    async function load() {
      if (!user || !companyIdResolved) return
      try {
        const result = await loadVision(companyId)
        setData(migrateLoaded(result?.vision_data))
      } catch {
        setData(JSON.parse(JSON.stringify(DEFAULT)))
        setDbReady(false)
      }
      setLoading(false)
    }
    load()
  }, [user?.id, companyId, companyIdResolved])

  useEffect(() => {
    if (!data || loading || !dbReady) return
    if (previewUserId) return // read-only in admin preview
    if (!companyId) return
    setSaveStatus('unsaved')
    const t = setTimeout(async () => {
      setSaveStatus('saving')
      await saveVision(companyId, data)
      setSaveStatus('saved')
    }, 2000)
    return () => clearTimeout(t)
  }, [data]) // eslint-disable-line

  function update(field, value) {
    setData(prev => ({ ...prev, [field]: value }))
  }

  function updateArr(field, index, value) {
    setData(prev => {
      const arr = [...(prev[field] || [])]
      arr[index] = value
      return { ...prev, [field]: arr }
    })
  }

  function updateGoal(index, key, value) {
    setData(prev => {
      const arr = [...(prev.goals || [])]
      arr[index] = { ...(arr[index] || { text: '', owner: '' }), [key]: value }
      return { ...prev, goals: arr }
    })
  }

  function togglePower(id) {
    setData(prev => {
      const cur = prev.howWeWinPowers || []
      const next = cur.includes(id) ? cur.filter(x => x !== id) : [...cur, id]
      return { ...prev, howWeWinPowers: next }
    })
  }

  // Completion tracking — covers all six elements.
  function getCompletion() {
    if (!data) return 0
    const checks = [
      (data.coreValues || []).some(v => v),
      data.purpose,
      data.winningAspiration,
      data.wherePlaySegment || data.wherePlayChannel || data.wherePlayProduct,
      (data.howWeWinPowers || []).length > 0 || data.howWeWinDescription,
      (data.goals || []).some(g => g.text),
    ]
    return Math.round((checks.filter(Boolean).length / checks.length) * 100)
  }

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
      <div style={{ fontSize: 14, color: '#888' }}>Loading Blueprint…</div>
    </div>
  )

  const completion = getCompletion()

  return (
    <div style={{ padding: 32, maxWidth: 800, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 600, color: '#2C2C2A', marginBottom: 4 }}>The Blueprint</h1>
          <div style={{ fontSize: 12, color: '#888780' }}>Six choices. One company that knows where it's going.</div>
        </div>
        <span style={{ fontSize: 11, color: saveStatus === 'saved' ? '#3B6D11' : saveStatus === 'saving' ? '#854F0B' : '#888' }}>
          {saveStatus === 'saved' && '✓ Saved'}
          {saveStatus === 'saving' && '↑ Saving…'}
          {saveStatus === 'unsaved' && '● Unsaved'}
        </span>
      </div>

      {/* Completion bar */}
      <div style={{ marginBottom: 28 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#888780', marginBottom: 6 }}>
          <span>Blueprint completion</span>
          <span style={{ fontWeight: 500, color: completion === 100 ? '#166534' : '#2C2C2A' }}>{completion}%</span>
        </div>
        <div style={{ height: 4, background: '#EBE7DE', borderRadius: 2 }}>
          <div style={{ height: 4, background: completion === 100 ? '#22C55E' : '#3B6D11', borderRadius: 2, width: `${completion}%`, transition: 'width .4s' }} />
        </div>
      </div>

      {!dbReady && (
        <div style={{ background: '#FEF2F2', border: '1px solid #FCA5A5', borderRadius: 8, padding: '14px 18px', marginBottom: 24, fontSize: 13, color: '#991B1B' }}>
          <strong>Setup needed:</strong> Run the vision_maps SQL in Supabase to enable saving.
        </div>
      )}

      {/* Tabs — three layers of the Blueprint */}
      <div style={{ display: 'flex', gap: 2, marginBottom: 28, background: '#F1EFE8', borderRadius: 8, padding: 4 }}>
        {[
          { id: 'identity',  label: 'Identity' },
          { id: 'strategy',  label: 'Strategy' },
          { id: 'execution', label: 'Execution' },
        ].map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
            flex: 1, padding: '8px 0', fontSize: 13, fontWeight: activeTab === tab.id ? 600 : 400,
            border: 'none', borderRadius: 6, cursor: 'pointer',
            background: activeTab === tab.id ? '#1E3320' : 'transparent',
            color: activeTab === tab.id ? '#fff' : '#888780',
            transition: 'all .15s'
          }}>{tab.label}</button>
        ))}
      </div>

      {/* ════════════════ IDENTITY ════════════════ */}
      {activeTab === 'identity' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

          <BpCard
            number="01"
            title="Core values"
            subtitle="3–7 behaviors that describe how the best people on the team already act. Not aspirations — observable patterns."
          >
            <Field label="Values">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {(data.coreValues || []).map((v, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 12, color: '#aaa', width: 16, textAlign: 'right', flexShrink: 0 }}>{i + 1}</span>
                    <input value={v} onChange={e => updateArr('coreValues', i, e.target.value)}
                      placeholder={`Core value ${i + 1}…`} style={lineInput} />
                    {i >= 3 && (
                      <button onClick={() => {
                        const arr = data.coreValues.filter((_, ci) => ci !== i)
                        update('coreValues', arr)
                      }} style={removeBtn}>×</button>
                    )}
                  </div>
                ))}
                <button onClick={() => update('coreValues', [...(data.coreValues || []), ''])} style={addBtn}>+ Add value</button>
              </div>
            </Field>
          </BpCard>

          <BpCard
            number="02"
            title="Purpose"
            subtitle="One sentence. Why the company exists, beyond making money."
          >
            <Field label="Purpose">
              <textarea
                value={data.purpose || ''}
                onChange={e => update('purpose', e.target.value)}
                placeholder="We exist to…"
                style={{ ...areaInput, minHeight: 80 }}
              />
            </Field>
            <Helper>
              <strong>Test it:</strong> A purpose works if a new hire could repeat it on day one and a customer wouldn't roll their eyes at it. Generic verbs like "empower" and "transform" usually mean it isn't done yet.
            </Helper>
          </BpCard>

        </div>
      )}

      {/* ════════════════ STRATEGY ════════════════ */}
      {activeTab === 'strategy' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

          <BpCard
            number="03"
            title="Winning aspiration"
            subtitle="A vivid 3–5 year picture of what winning looks like. Not a revenue number — the state of the company, the customer experience, the standing in the market."
          >
            <Field label="What does winning look like?">
              <textarea
                value={data.winningAspiration || ''}
                onChange={e => update('winningAspiration', e.target.value)}
                placeholder="In 3–5 years…"
                style={{ ...areaInput, minHeight: 90 }}
              />
            </Field>
            <Field label="What does it feel like? — describe the company at that point">
              <textarea
                value={data.aspirationDetail || ''}
                onChange={e => update('aspirationDetail', e.target.value)}
                placeholder="The team is… The product is… Customers say…"
                style={{ ...areaInput, minHeight: 100 }}
              />
            </Field>
          </BpCard>

          <BpCard
            number="04"
            title="Where we play"
            subtitle="The playing field. Each choice should disqualify other options. If your answer fits everyone, it isn't a choice."
          >
            <Field label="Customer segment — who specifically do we serve?">
              <textarea
                value={data.wherePlaySegment || ''}
                onChange={e => update('wherePlaySegment', e.target.value)}
                placeholder="Our customer is…"
                style={{ ...areaInput, minHeight: 70 }}
              />
            </Field>
            <Field label="Geography / channel — where and how do we reach them?">
              <textarea
                value={data.wherePlayChannel || ''}
                onChange={e => update('wherePlayChannel', e.target.value)}
                placeholder="We reach them through…"
                style={{ ...areaInput, minHeight: 70 }}
              />
            </Field>
            <Field label="Product / offering — what specifically do we sell?">
              <textarea
                value={data.wherePlayProduct || ''}
                onChange={e => update('wherePlayProduct', e.target.value)}
                placeholder="We offer…"
                style={{ ...areaInput, minHeight: 70 }}
              />
            </Field>
            <Field label="Where we explicitly won't play">
              <textarea
                value={data.wherePlayWont || ''}
                onChange={e => update('wherePlayWont', e.target.value)}
                placeholder="We will not pursue…"
                style={{ ...areaInput, minHeight: 70 }}
              />
            </Field>
          </BpCard>

          <BpCard
            number="05"
            title="How we win"
            subtitle="The source of durable advantage. Pick one — two if you must. A power is real only if a competitor would have to give something up to copy it."
          >
            <Field label="Source of advantage (Helmer's 7 Powers — pick 1–2)">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {POWERS.map(p => {
                  const checked = (data.howWeWinPowers || []).includes(p.id)
                  return (
                    <label key={p.id} style={{
                      display: 'flex', gap: 10, padding: '8px 10px', borderRadius: 6,
                      cursor: 'pointer', border: '1px solid ' + (checked ? '#3B6D11' : '#EBE7DE'),
                      background: checked ? '#F4F8EE' : '#fff', transition: 'all .12s'
                    }}>
                      <input type="checkbox" checked={checked} onChange={() => togglePower(p.id)}
                        style={{ marginTop: 3, accentColor: '#3B6D11', flexShrink: 0 }} />
                      <span style={{ fontSize: 12, lineHeight: 1.45, color: '#2C2C2A' }}>
                        <strong style={{ fontWeight: 600 }}>{p.name}.</strong>{' '}
                        <span style={{ color: '#5F5E5A' }}>{p.desc}</span>
                      </span>
                    </label>
                  )
                })}
              </div>
            </Field>

            {(data.howWeWinPowers || []).includes('other') && (
              <Field label="Name your &ldquo;other / hybrid&rdquo; power">
                <input
                  value={data.howWeWinOther || ''}
                  onChange={e => update('howWeWinOther', e.target.value)}
                  placeholder="What is it, and why is it durable?"
                  style={lineInput}
                />
              </Field>
            )}

            <Field label="In plain language — how this company wins">
              <textarea
                value={data.howWeWinDescription || ''}
                onChange={e => update('howWeWinDescription', e.target.value)}
                placeholder="When a customer compares us to alternatives, we win because…"
                style={{ ...areaInput, minHeight: 90 }}
              />
            </Field>
          </BpCard>

        </div>
      )}

      {/* ════════════════ EXECUTION ════════════════ */}
      {activeTab === 'execution' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

          <BpCard
            number="06"
            title="The next twelve months"
            subtitle="The 1-year plan is the bridge between strategy and the calendar. Every goal should ladder back to the strategy above it."
          >
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1.2fr', gap: 16, marginBottom: 16 }}>
              <Field label="Year">
                <input
                  value={data.year || ''}
                  onChange={e => update('year', e.target.value)}
                  placeholder="e.g. 2026"
                  style={lineInput}
                />
              </Field>
              <Field label="Revenue target">
                <input
                  value={data.revenueTarget || ''}
                  onChange={e => update('revenueTarget', e.target.value)}
                  placeholder="e.g. $2M ARR"
                  style={lineInput}
                />
              </Field>
              <Field label="Profit / other key metric">
                <input
                  value={data.keyMetric || ''}
                  onChange={e => update('keyMetric', e.target.value)}
                  placeholder="e.g. 15% net margin, 500 customers"
                  style={lineInput}
                />
              </Field>
            </div>

            <Field label="Goals for the year — 3–7 items, each with one owner">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {(data.goals || []).map((g, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 12, color: '#aaa', width: 16, textAlign: 'right', flexShrink: 0 }}>{i + 1}</span>
                    <input value={g.text || ''} onChange={e => updateGoal(i, 'text', e.target.value)}
                      placeholder={`Goal ${i + 1}…`} style={{ ...lineInput, flex: 1 }} />
                    <input value={g.owner || ''} onChange={e => updateGoal(i, 'owner', e.target.value)}
                      placeholder="Owner" style={{ ...lineInput, width: 140 }} />
                    {i >= 3 && (
                      <button onClick={() => {
                        const arr = (data.goals || []).filter((_, gi) => gi !== i)
                        update('goals', arr)
                      }} style={removeBtn}>×</button>
                    )}
                  </div>
                ))}
                <button onClick={() => update('goals', [...(data.goals || []), { text: '', owner: '' }])} style={addBtn}>+ Add goal</button>
              </div>
            </Field>

            <Field label="What this year is NOT about">
              <textarea
                value={data.notAbout || ''}
                onChange={e => update('notAbout', e.target.value)}
                placeholder="The things you'll be tempted to chase but won't…"
                style={{ ...areaInput, minHeight: 80 }}
              />
            </Field>

            <Helper>
              From here, the 1-year plan cascades into 90-Day Priorities, weekly Scorecard metrics, and The Weekly meeting. The Blueprint sets direction; the rhythm gets it done.
            </Helper>
          </BpCard>

        </div>
      )}
    </div>
  )
}

// ─── Subcomponents ──────────────────────────────────────────────────────

function BpCard({ number, title, subtitle, children }) {
  return (
    <div style={{ border: '1px solid #EBE7DE', borderRadius: 10, overflow: 'hidden' }}>
      <div style={{ padding: '16px 20px', background: '#1E3320', display: 'flex', alignItems: 'flex-start', gap: 16 }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,0.3)', letterSpacing: '.08em', marginTop: 2, flexShrink: 0 }}>{number}</span>
        <div>
          <div style={{ fontSize: 15, fontWeight: 600, color: '#fff' }}>{title}</div>
          {subtitle && <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginTop: 3, lineHeight: 1.4 }}>{subtitle}</div>}
        </div>
      </div>
      <div style={{ padding: '20px' }}>{children}</div>
    </div>
  )
}

function Field({ label, children, style }) {
  return (
    <div style={{ marginBottom: 16, ...style }}>
      <label style={{ fontSize: 11, fontWeight: 500, color: '#888780', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '.06em' }}>{label}</label>
      {children}
    </div>
  )
}

function Helper({ children }) {
  return (
    <div style={{
      background: '#F1EFE8', borderLeft: '3px solid #3B6D11',
      padding: '10px 14px', fontSize: 12, color: '#5F5E5A',
      lineHeight: 1.5, borderRadius: 4
    }}>
      {children}
    </div>
  )
}

// ─── Styles ─────────────────────────────────────────────────────────────

const lineInput = {
  width: '100%', fontSize: 13, border: '1px solid #EBE7DE', borderRadius: 6,
  padding: '8px 10px', outline: 'none', background: '#fff', color: '#2C2C2A', fontFamily: 'inherit'
}

const areaInput = {
  width: '100%', fontSize: 13, border: '1px solid #EBE7DE', borderRadius: 6,
  padding: '10px 12px', outline: 'none', background: '#fff', color: '#2C2C2A',
  fontFamily: 'inherit', resize: 'vertical', lineHeight: 1.75
}

const addBtn = {
  alignSelf: 'flex-start', fontSize: 11, padding: '4px 10px', borderRadius: 5,
  border: '1px solid #D0CDC7', background: 'transparent', color: '#888', cursor: 'pointer', marginTop: 2
}

const removeBtn = {
  border: 'none', background: 'none', cursor: 'pointer', color: '#ddd',
  fontSize: 16, padding: 0, flexShrink: 0
}
